import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Card, Typography, Stack, Chip, Grid, Button, Divider } from '@mui/material';
import { NotificationManager } from 'react-notifications';
import { formatDate, formatCurrencyBRL } from '../../../../@core/utils/utilsFunctions';
import { CustomGrid, CustomInfosForm } from '../../../../assets/style/globalStyle';
import configAxios from '../../../../services/configAxios';
import Iconify from '../../../../@core/theme/Iconify';
import RenderPage from '../../../layouts/RenderPage';
import BreadcrumbsNav from '../../../components/multiplesComponents/BreadCrumbs';
import Scrollbar from '../../../components/multiplesComponents/Scrollbar';
import PipelineFilters from './PipelineFilters';
import { PIPELINE_STATUSES, getStatusMeta, buildPipelineQuery } from './pipelineStatuses';

const STATUS_ORDER = PIPELINE_STATUSES.map((item) => item.status);

const PipelineKanban = (props) => {
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState({});

  useEffect(() => {
    getKanban();
  }, []);

  function getKanban(nextFilters = filters) {
    setLoading(true);
    configAxios
      .get(`/pipeline/kanban${buildPipelineQuery(nextFilters)}`)
      .then((response) => {
        const grouped = {};
        STATUS_ORDER.forEach((status) => {
          grouped[status] = [];
        });
        (response.data || []).forEach((item) => {
          const status = item.statusvenda || 'Outros';
          if (!grouped[status]) {
            grouped[status] = [];
          }
          grouped[status].push(item);
        });
        setColumns(grouped);
      })
      .catch(() => {
        NotificationManager.warning(
          'Tivemos dificuldades para buscar o kanban, por favor, tente novamente!',
          'Atenção'
        );
      })
      .finally(() => setLoading(false));
  }

  const totalCards = Object.values(columns).reduce((sum, list) => sum + (list || []).length, 0);

  return (
    <RenderPage title="Kanban">
      <>
        <Stack direction="row" alignItems="center" justifyContent="start" mb={2}>
          <Grid container>
            <BreadcrumbsNav {...props} />
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="h4" gutterBottom>
                Kanban
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Utilize os filtros abaixo para visualizar as vendas por status
              </Typography>
            </Grid>
          </Grid>
          <Stack direction="row" alignItems="center" justifyContent="end" mb={2}>
            <Button
              variant="outlined"
              onClick={() => getKanban(filters)}
              disabled={loading}
              startIcon={<Iconify icon="eva:refresh-outline" />}>
              Atualizar
            </Button>
          </Stack>
        </Stack>

        <Card>
          <CustomGrid>
            <PipelineFilters
              onFilter={(nextFilters) => {
                setFilters(nextFilters);
                getKanban(nextFilters);
              }}
              loading={loading}
            />
            <CustomInfosForm sx={{ mt: 2 }}>
              {totalCards} proposta(s) encontrada(s). Clique no card para abrir a venda.
            </CustomInfosForm>
          </CustomGrid>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CustomGrid>
            <Box sx={{ height: 'calc(100vh - 320px)' }}>
              <Scrollbar
                sx={{
                  height: 1,
                  '& .simplebar-content': {
                    height: 1,
                    display: 'flex',
                    gap: 2,
                    alignItems: 'stretch',
                    pb: 1
                  }
                }}>
                {STATUS_ORDER.map((status) => {
                  const cards = columns[status] || [];
                  const statusColor = getStatusMeta(status).color;
                  const totalValor = cards.reduce(
                    (sum, card) => sum + Number(card.valclient || 0),
                    0
                  );

                  return (
                    <Box
                      key={status}
                      sx={{
                        width: 260,
                        flex: '0 0 260px',
                        height: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0
                      }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                        mb={1}>
                        <Chip
                          size="small"
                          color={statusColor}
                          label={status}
                          sx={{ maxWidth: 180 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {cards.length}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Total: {formatCurrencyBRL(totalValor)}
                      </Typography>
                      <Divider sx={{ my: 1.5 }} />

                      <Box sx={{ flex: 1, minHeight: 0, height: 0 }}>
                        <Scrollbar
                          sx={{
                            height: 1,
                            '& .simplebar-content': {
                              height: 'auto',
                              display: 'block'
                            }
                          }}>
                          <Stack spacing={1.5} sx={{ pt: 0.5, pr: 1.5, pb: 0.5 }}>
                            {!cards.length ? (
                              <Typography variant="body2" color="text.secondary">
                                Nenhuma proposta
                              </Typography>
                            ) : (
                              cards.map((card) => (
                                <Card
                                  key={String(card.id)}
                                  component={RouterLink}
                                  to={`/app/editClients/${card.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="outlined"
                                  sx={{
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    flexShrink: 0
                                  }}>
                                  <CustomGrid>
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      spacing={1}
                                      mb={0.5}>
                                      <Typography variant="caption" color="text.secondary">
                                        {card.codclient || '-'}
                                      </Typography>
                                      <Chip
                                        size="small"
                                        color={statusColor}
                                        label={formatCurrencyBRL(card.valclient)}
                                      />
                                    </Stack>
                                    <Typography
                                      variant="subtitle2"
                                      title={card.nome || '-'}
                                      gutterBottom
                                      noWrap>
                                      {card.nome || '-'}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      noWrap>
                                      {card.tpcontrat_client || '-'}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      noWrap>
                                      {card.nomevend || '-'}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      noWrap>
                                      {card.descplano || '-'}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      noWrap>
                                      Cadastro:{' '}
                                      {card.datacadastro ? formatDate(card.datacadastro) : '-'}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      noWrap>
                                      Vigência:{' '}
                                      {card.dtvigencia ? formatDate(card.dtvigencia) : '-'}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      noWrap
                                      fontWeight={700}>
                                      Dias no status: {card.dias_status_atual ?? '-'}
                                    </Typography>
                                  </CustomGrid>
                                </Card>
                              ))
                            )}
                          </Stack>
                        </Scrollbar>
                      </Box>
                    </Box>
                  );
                })}
              </Scrollbar>
            </Box>
          </CustomGrid>
        </Card>
      </>
    </RenderPage>
  );
};

export default PipelineKanban;
